import { getViewportForBounds, type Rect, type Viewport } from "@xyflow/react";

/**
 * Encuadre del grafo de modelos: la parte PURA, sin React Flow corriendo.
 *
 * Vive aparte de `GrafoModelos.tsx` por lo mismo que `grafoLayout.ts`: la
 * regla se prueba aqui con numeros, y que el canvas la aplique se comprueba en
 * Chrome (checklist del ESTADO).
 *
 * **Por que se calcula el viewport a mano y no con `fitView()`**: desde React
 * Flow 12.x, el `fitView` de `useReactFlow` es una PROMESA encolada que se
 * resuelve en el siguiente `setNodes` con los nodos medidos. La primera vez
 * (al montar) se resuelve al medirlos; la segunda —tras entrar en pantalla
 * completa, con los nodos ya medidos y la topologia controlada por fuera con
 * `onNodesChange` vacio— **no se resolvia**: medido en Chrome con el arnes, el
 * re-encuadre arrancaba (`h=745`) y la promesa se quedaba colgada, con lo que
 * el grafo seguia encuadrado para el alto ANTERIOR (688) y nadie se enteraba.
 * `getViewportForBounds` es la misma aritmetica que usa `fitView` por dentro,
 * exportada como API publica, y `setViewport` es sincrono: sin cola, sin
 * promesa que pueda no volver, y ademas testeable con numeros.
 */

/** Padding del encuadre, fraccion del canvas que queda libre alrededor. */
export const PADDING_ENCUADRE = 0.1;
/**
 * Topes del zoom de ENCUADRE (no del canvas: ese es cuanto puede alejar A
 * MANO y lo fija `minZoom` de `<ReactFlow>`).
 *
 * `MAX`: sin tope, dos nodos sueltos se pintarian a tamaño gigante. `MIN`: sin
 * tope, el encuadre encoge hasta que todo quepa y "se ve entero" deja de
 * significar nada en cuanto el texto es ilegible; 0,45 es donde el grafo real
 * (28 cajas) deja de leerse. Con el tope, si no cabe entero se ve legible y el
 * usuario recorre el resto con el pan — que es la salida honesta.
 */
export const ZOOM_ENCUADRE = { min: 0.45, max: 1 } as const;

/** Margen, en px de pantalla, que queda sobre el grafo cuando se alinea arriba. */
export const MARGEN_ENCUADRE_PX = 12;

/**
 * Viewport alineado ARRIBA cuando el grafo no cabe a lo alto, o `null` si cabe
 * y el centrado vale tal cual.
 *
 * El encuadre centra siempre, y con el zoom clavado en su minimo el grafo real
 * en la vista de pagina mide mas que el canvas; centrado, lo que se corta es
 * justo la cabecera con "Agentes" y "Tareas" —medido en Chrome a 1400x900: los
 * dos titulos fuera del canvas— y los grupos pierden su nombre. Alineado
 * arriba se ven los titulos y las primeras filas, y el resto se recorre con el
 * pan hacia abajo, que es la direccion que uno espera.
 *
 * @param limites  rectangulo de todos los nodos, en coordenadas del grafo
 * @param viewport el centrado (se conservan `x` y `zoom`)
 * @param altoCanvas alto del canvas en px de pantalla
 */
export function alinearArriba(
  limites: Pick<Rect, "y" | "height">,
  viewport: Viewport,
  altoCanvas: number,
): Viewport | null {
  if (limites.height * viewport.zoom <= altoCanvas) return null;
  return {
    x: viewport.x,
    y: MARGEN_ENCUADRE_PX - limites.y * viewport.zoom,
    zoom: viewport.zoom,
  };
}

/**
 * El viewport que encuadra `limites` en un canvas de `ancho` x `alto`: la
 * aritmetica de `fitView` (misma funcion, mismos topes) y, si a ese zoom el
 * grafo no cabe a lo alto, alineado arriba.
 */
export function calcularEncuadre(limites: Rect, ancho: number, alto: number): Viewport {
  const centrado = getViewportForBounds(
    limites,
    ancho,
    alto,
    ZOOM_ENCUADRE.min,
    ZOOM_ENCUADRE.max,
    PADDING_ENCUADRE,
  );
  return alinearArriba(limites, centrado, alto) ?? centrado;
}
